import React, { useEffect, useMemo, useRef } from 'react';
import { runInAction } from 'mobx';

import Steps from 'Views/Components/Steps/Steps';
import { useWizardContext } from 'Views/Components/Wizard/WizardContext';

import { IStudyPlanWizardData } from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlanWizard';
import Wizard from 'Views/Components/Wizard/Wizard';

import StudyPlanWizardHeader
	from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlanWizardHeader';
import StudyPlanWizardSidebar
	from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlanWizardSidebar';
import CourseSelectionPageFooter from 'Views/Pages/Frontend/StudyPlanWizard/CourseSelection/CourseSelectionPageFooter';
import RequirementList from 'Views/Pages/Frontend/StudyPlanWizard/CourseSelection/RequirementList';

import { CourseOfferingEntity, PlanSelectionEntity } from 'Models/Entities';

import { indexByPropertyArray } from 'Util/DataIndexUtils';

import CourseSelectionFilterInput
	from 'Views/Pages/Frontend/StudyPlanWizard/CourseSelection/CourseSelectionFilterInput';
import { useRequirementIndex }
	from 'Views/Pages/Frontend/StudyPlanWizard/RequirementIndexContext';
import { observer } from 'mobx-react';
import { toLower } from 'lodash';

const CourseSelectionPage = observer(() => {
	const contentElement = useRef<HTMLDivElement>(null);

	const {
		step,
		stepLabels,
		data,
		setAndSaveData,
		previousStep,
		nextStep,
		goToStep,
	} = useWizardContext<IStudyPlanWizardData>();

	const {
		planSelections,
		setPlanSelections,
		components,
		searchAndFilterState,
		setSearchAndFilterState,
		resetSearchAndFilterState,
		availableCourseFilters,
		currentComponentId,
		setCurrentComponentId,
		requirementsById,
		generatedPlanFeedback,
		courseOfferingsById,
		courseOfferingIdByCourseIdByRequirementId,
	} = useRequirementIndex();

	useEffect(() => {
		return () => {
			// Reset and filter state when component unmounts
			resetSearchAndFilterState();
		};
	}, [resetSearchAndFilterState]);

	const currentComponent = requirementsById[currentComponentId || '']
		? requirementsById[currentComponentId || '']
		: undefined;

	const requisiteWarningsByCourseId = useMemo(
		() => generatedPlanFeedback
			? indexByPropertyArray(
				generatedPlanFeedback.requisiteWarnings,
				warning => warning.courseId,
			) : {},
		[generatedPlanFeedback],
	);

	/**
	 * Determine the default course offering entity to select
	 * using the plan's preferred campus
	 *
	 * The order of preference is:
	 * - The first course offering that matches the preferred campus and is the default term
	 * - The first course offering that matches the preferred campus (but not the default term)
	 * - The first course offering in the campus options AND is the default term
	 * - The first course offering in the campus options
	 * @param requirementId
	 * @param courseId
	 * @param preferredCampusId
	 */
	const getDefaultSelectedOffering = (
		requirementId: string,
		courseId: string,
		preferredCampusId: string,
	): CourseOfferingEntity | undefined => {
		const coursesOfRequirement = courseOfferingIdByCourseIdByRequirementId[requirementId];

		// Get only enabled course offerings of the given course
		const courseOfferingsForSelection = coursesOfRequirement[courseId]
			.map(courseOfferingId => courseOfferingsById[courseOfferingId])
			.filter(courseOffering => courseOffering.enabled);

		if (courseOfferingsForSelection.length === 0) {
			return undefined;
		}

		const campuses = courseOfferingsForSelection
			.map(offering => offering.campus)
			.sort((a, b) => a.name.localeCompare(b.name));

		const terms = courseOfferingsForSelection
			.map(offering => offering.term)
			.sort((a, b) => ((a.studyPeriod.order * 1000) + a.order) - ((b.studyPeriod.order * 1000) + b.order));

		// Get offerings matching the preferred campus
		const offeringsMatchingPreferredCampus = courseOfferingsForSelection
			.filter(offering => offering.campusId === preferredCampusId)
			.sort((a, b) => a.campus.name.localeCompare(b.campus.name));

		// When there is an offering that matches the preferred campus
		if (offeringsMatchingPreferredCampus.length > 0) {
			const defaultTermOffering = offeringsMatchingPreferredCampus
				.find(offering => offering.isDefaultTerm);

			// If an offering that match the preferred campus and is the default term exists, return it
			if (defaultTermOffering) {
				return defaultTermOffering;
			}

			// If not, return the first offering of the earliest term
			return offeringsMatchingPreferredCampus.find(offering => offering.termId === terms[0].id);
		}

		// When no offerings that match the preferred campus,
		// return the  offering of the first campus that has a default term
		const defaultTermOffering = courseOfferingsForSelection
			.find(offering => offering.campusId === campuses[0].id && offering.isDefaultTerm);

		if (defaultTermOffering) {
			return defaultTermOffering;
		}

		// When no offerings that match the preferred campus and no default term offering exists
		return courseOfferingsForSelection.find(
			offering => offering.campusId === campuses[0].id,
		);
	};

	// whenever selected or completed courses are changed
	// map them to entities attached to the study plan and save them
	const saveSelectedCoursesToWizardData = async (
		locationId: string,
		courseId: string,
		enabled: boolean,
		completed: boolean,
		selectedCourseOfferingId?: string,
	) => {
		if (!data?.studyPlan?.id || !data.studyPlan) {
			return;
		}

		const updatedPlanSelections: PlanSelectionEntity[] = [];

		switch (enabled) {
			case true: {
				// Copy the existing plan selections
				updatedPlanSelections.push(...planSelections);

				const existingPlanSelection = updatedPlanSelections
					.find(
						planSelection => planSelection.courseId === courseId
							&& planSelection.locationId === locationId,
					);

				if (!existingPlanSelection) {
					// If the course is not already selected, create new
					const newSelection = new PlanSelectionEntity({
						studyPlanId: data?.studyPlan?.id,
						locationId: locationId,
						courseId: courseId,
						courseOfferingId: selectedCourseOfferingId,
					});

					updatedPlanSelections.push(newSelection);
				} else {
					existingPlanSelection.courseOfferingId = selectedCourseOfferingId;
				}

				updatedPlanSelections.map(planSelection => {
					if (planSelection.courseId === courseId) {
						planSelection.completionStatus = completed ? 'COMPLETE' : 'INCOMPLETE';

						if (!completed && !planSelection.courseOfferingId) {
							// Select the default course offering for the course if it is not already selected
							planSelection.courseOfferingId = getDefaultSelectedOffering(
								planSelection.locationId,
								planSelection.courseId,
								data.studyPlan!.campusId,
							)?.id;
						}
					}

					return new PlanSelectionEntity(planSelection);
				});

				break;
			}
			case false: {
				// If the course is not enabled, remove it from the list
				updatedPlanSelections.push(...planSelections
					.filter(
						planSelection => !(planSelection.courseId === courseId
							&& planSelection.locationId === locationId),
					));
				break;
			}
			default:
				throw new Error('Invalid course selection state');
		}

		setPlanSelections(updatedPlanSelections);

		runInAction(() => {
			data!.studyPlan!.planSelections = updatedPlanSelections;
		});

		await setAndSaveData({ ...data });

		return updatedPlanSelections;
	};

	/**
	 * When the current component changes,
	 * ensure the content is scrolled to the top
	 */
	useEffect(() => {
		if (contentElement.current) {
			contentElement.current.scrollTop = 0;
		}
	}, [currentComponent?.id]);

	return (
		<>
			<StudyPlanWizardHeader />
			<Wizard.Body>
				<StudyPlanWizardSidebar
					showProgramDetails
					studyPlan={data?.studyPlan}
					onComponentClick={componentId => setCurrentComponentId(componentId)}
					currentComponentId={currentComponentId}
				/>
				<Steps
					currentStep={step}
					steps={stepLabels}
					onStepClick={goToStep}
				/>
				<Wizard.Main>
					<div className="content step-2" ref={contentElement}>
						<div className="header-wrapper">
							<h3>Select {toLower(window.lexicon.courses)} - {currentComponent?.displayName}</h3>
							<p className="sub-header">{currentComponent?.description}</p>
						</div>
						<CourseSelectionFilterInput
							searchAndFilterState={searchAndFilterState}
							availableCourseFilters={availableCourseFilters}
							onSearchAndFilter={
								searchAndFilterValues => setSearchAndFilterState({ ...searchAndFilterValues })
							}
						/>
						<RequirementList
							currentComponent={currentComponent}
							requisiteWarningsByCourseId={requisiteWarningsByCourseId}
							updatePlanSelections={saveSelectedCoursesToWizardData}
						/>
					</div>
					<CourseSelectionPageFooter
						components={components}
						currentComponentId={currentComponentId}
						nextStep={nextStep}
						previousStep={previousStep}
						setCurrentComponentId={setCurrentComponentId}
					/>
				</Wizard.Main>
			</Wizard.Body>
		</>
	);
});

export default CourseSelectionPage;
